import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { AuthenticationFacade } from '@planone/bau365/data-access-authentication';
import * as DataAccessFairData from '@planone/bau365/data-access-fair-data';
import { DataAccessExhibitorsFacade, DataAccessProductsFacade } from '@planone/bau365/data-access-fair-data';
import { Exhibitor, Product } from '@planone/bau365/util-fair-data';
import * as FeatureTrackingGoogleAnalytics from '@planone/shared/feature-tracking-google-analytics';
import * as FeatureTrackingSnowplow from '@planone/shared/feature-tracking-snowplow';
import { localizedMeta, MetaService } from '@planone/shared/util-meta';
import { delay, filter, map, of, switchMap, take, withLatestFrom } from 'rxjs';

import { DataTransformationHelper } from '../../helper/data-transformation.helpers';
import { CustomEventIglu } from '../../tracking-types/snowplow/schema/schema.interface';

@Injectable()
export class ProductsEffects {
  pageViewDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DataAccessFairData.productOpened),
      switchMap((action) => {
        return this.productsFacade.product$.pipe(
          filter((product) => !!product && product.id === action.productUuid),
          take(1)
        );
      }),
      switchMap((product) =>
        this.exhibitorsFacade.getExhibitorById(product?.exhibitor_id).pipe(
          filter((exhibitor) => !!exhibitor),
          take(1),
          withLatestFrom(this.authenticationFacade.excludeAnalytics$),
          map(
            ([exhibitor, exclude_analytics]) => <[Product, Exhibitor, boolean]>[product, exhibitor, exclude_analytics]
          ),
          delay(0),
          switchMap(([product, exhibitor, exclude_analytics]) => {
            const title = this.metaService.getTitle(localizedMeta['titleProductDetails'], [product.name ?? '']);

            const snowplowExhibitorContext = this.dataTransformationHelper.snowplow?.map('EXHIBITOR', exhibitor);
            const snowplowProductContext = this.dataTransformationHelper.snowplow?.map(
              'PRODUCT',
              product,
              exhibitor.fair_id
            );

            const snowplowContext = [
              {
                schema: CustomEventIglu.page_view,
                data: {
                  context: exhibitor.fair_id,
                  page_type: 'product-details',
                  exclude_analytics: exclude_analytics,
                },
              },
            ];

            if (snowplowExhibitorContext) snowplowContext.push(snowplowExhibitorContext);
            if (snowplowProductContext) snowplowContext.push(snowplowProductContext);

            return of(
              FeatureTrackingSnowplow.pageView({
                title: title,
                context: snowplowContext ? snowplowContext : undefined,
              }),
              FeatureTrackingGoogleAnalytics.push({
                event: 'pageview',
                context: exhibitor.fair_id,
                page_type: 'product-details',
                exclude_analytics: exclude_analytics,
                ...this.dataTransformationHelper.googleAnalytics?.map('EXHIBITOR', exhibitor),
                ...this.dataTransformationHelper.googleAnalytics?.map('PRODUCT', product),
              })
            );
          })
        )
      )
    )
  );

  constructor(
    private readonly actions$: Actions,
    private store: Store,
    private authenticationFacade: AuthenticationFacade,
    private productsFacade: DataAccessProductsFacade,
    private exhibitorsFacade: DataAccessExhibitorsFacade,
    private metaService: MetaService,
    private dataTransformationHelper: DataTransformationHelper
  ) {}
}
